import { useEffect, useState } from "react";
import { getPortalUrl } from "../lib/portal-url";
import "./paywall.css";

export interface GuestAccessPromptProps {
  open: boolean;
  onClose: () => void;
  portalUrl?: string;
  redirectTo: string;
}

export function GuestAccessPrompt({
  open,
  onClose,
  portalUrl,
  redirectTo,
}: GuestAccessPromptProps) {
  const portal = getPortalUrl(portalUrl);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setLoading(false);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!window.PortalAuth?.requestGuestAccess) {
      setError("Registratie is momenteel niet beschikbaar.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await window.PortalAuth.requestGuestAccess(email.trim(), redirectTo);
      setSuccess(true);
    } catch {
      setError("Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="jari-paywall-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-access-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="jari-paywall-modal">
        <h2 id="guest-access-title">Onbeperkt gratis verder?</h2>
        {success ? (
          <>
            <p>
              Check je e-mail voor de link. Na bevestiging krijg je onbeperkt toegang tot de
              gratis versie en tot ons tool portal.
            </p>
            <div className="jari-paywall-modal__actions">
              <button type="button" className="jari-btn" onClick={onClose}>
                Sluiten
              </button>
            </div>
          </>
        ) : (
          <>
            <p>
              Je kunt onbeperkt toegang krijgen tot de gratis versie als je je e-mailadres
              achterlaat. Daarmee krijg je ook toegang tot onze tool portal, waar alle nieuwe
              tools als eerste opgezet worden.
            </p>
            <form onSubmit={handleSubmit}>
              <label className="jari-guest-access__label" htmlFor="guest-access-email">
                E-mailadres
              </label>
              <input
                id="guest-access-email"
                type="email"
                className="jari-guest-access__input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={loading}
              />
              {error && <p className="jari-guest-access__error">{error}</p>}
              <div className="jari-paywall-modal__actions">
                <button
                  type="submit"
                  className="jari-btn jari-btn--primary"
                  disabled={loading || !email.trim()}
                >
                  {loading ? "Versturen…" : "Stuur registratielink"}
                </button>
                <button type="button" className="jari-btn" onClick={onClose} disabled={loading}>
                  Later
                </button>
              </div>
            </form>
            <p className="jari-guest-access__portal">
              Of ga naar het{" "}
              <a href={portal} target="_blank" rel="noopener noreferrer">
                portal
              </a>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
